import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Price, ProductWithPrice } from 'types';

import clsxm from '@/lib/clsxm';

import {
  ACTIONS_IN_PLAN,
  BOTS_IN_PLAN,
  CHARACTERS_IN_PLAN,
  products_for_free_plan,
  WORDS_IN_PLAN,
} from '@/constants/common';
import { postData } from '@/utils/helpers';
import { getStripe } from '@/utils/stripe-client';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  SectionHeading,
  tone,
} from './primitives';

type BillingInterval = 'month' | 'year';

// Teams and Unlimited 50 are sold on /pricing, not on this page.
const HIDDEN_PRODUCTS = new Set(['prod_OqT3UM05zlYR3V', 'prod_QskSrexOhYVHhJ']);
const CARD_PLANS = ['Starter', 'Unlimited', 'Unlimited 200'] as const;
const FEATURED_PLAN = 'Unlimited 200';
// Voice in the chat widget and removing the badge start at Unlimited 200.
const PREMIUM_EXTRAS_FROM = 200;

type PlanName = keyof typeof BOTS_IN_PLAN;

const compact = (value: number, locale?: string) =>
  new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

const plain = (value: number, locale?: string) =>
  new Intl.NumberFormat(locale).format(value);

const money = (cents: number, currency: string, locale?: string) => {
  const amount = cents / 100;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const planTier = (name: string) => Number(name.match(/\d+/)?.[0] ?? 0);

export default function AgentPricing() {
  const { t } = useLocale('chatbots');
  const router = useRouter();
  // Arabic defaults to Arabic-Indic digits; the page copy uses Latin ones.
  const locale = router.locale === 'ar' ? 'ar-u-nu-latn' : router.locale;
  const { user, subscription, isFreePlan } = useUser();
  const [products, setProducts] = useState<ProductWithPrice[]>([]);
  const [interval, setBillingInterval] = useState<BillingInterval>('year');
  const [loadingPrice, setLoadingPrice] = useState<string>();
  const [scaleIndex, setScaleIndex] = useState(0);

  useEffect(() => {
    const supabase = createClient();
    const ids = products_for_free_plan.filter((id) => !HIDDEN_PRODUCTS.has(id));
    supabase
      .from('products')
      .select('*, prices(*)')
      .in('id', ids)
      .eq('prices.active', true)
      .order('metadata->index')
      .order('unit_amount', { foreignTable: 'prices' })
      .then(({ data, error }) => {
        if (error) {
          console.error(error.message);
          return;
        }
        setProducts((data as ProductWithPrice[]) || []);
      });
  }, []);

  const priceFor = (product: ProductWithPrice, wanted: BillingInterval) =>
    product.prices?.find((price) => price.interval === wanted) as
      | Price
      | undefined;

  const byName = useMemo(
    () => new Map(products.map((product) => [product.name ?? '', product])),
    [products],
  );

  const scalePlans = useMemo(
    () =>
      products
        .filter(
          (product) =>
            !CARD_PLANS.includes(product.name as (typeof CARD_PLANS)[number]),
        )
        .sort((a, b) => planTier(a.name ?? '') - planTier(b.name ?? '')),
    [products],
  );

  // The best yearly saving across plans, for the toggle's badge.
  const yearlySaving = useMemo(() => {
    let best = 0;
    for (const product of products) {
      const monthly = priceFor(product, 'month')?.unit_amount;
      const yearly = priceFor(product, 'year')?.unit_amount;
      if (monthly && yearly) {
        best = Math.max(best, 1 - yearly / (monthly * 12));
      }
    }
    return Math.round(best * 100);
  }, [products]);

  const handleCheckout = async (price: Price) => {
    setLoadingPrice(price.id);
    if (!user) {
      router.push(`/login?price=${price.id}#auth-sign-up`);
      return;
    }
    try {
      const { sessionId } = await postData({
        url: '/api/create-checkout-session',
        data: isFreePlan
          ? { price: price.id }
          : { price: price.id, isUpgrade: true },
        token: '',
      });
      const stripe = await getStripe();
      stripe?.redirectToCheckout({ sessionId });
    } catch (error) {
      alert((error as Error)?.message);
    } finally {
      setLoadingPrice(undefined);
    }
  };

  const featuresFor = (name: string): string[] => {
    const plan = name as PlanName;
    const agents = BOTS_IN_PLAN[plan] ?? 1;
    const actions = ACTIONS_IN_PLAN[plan as keyof typeof ACTIONS_IN_PLAN] ?? 0;
    const characters =
      CHARACTERS_IN_PLAN[plan as keyof typeof CHARACTERS_IN_PLAN] ?? 0;
    const words = WORDS_IN_PLAN[plan as keyof typeof WORDS_IN_PLAN] ?? 0;
    const list = [
      t('{{n}} agents', { n: plain(agents, locale) }),
      t('{{n}} actions per agent', { n: plain(actions, locale) }),
      t('{{size}} characters of knowledge per agent', {
        size: compact(characters, locale),
      }),
      t('{{n}} words a month on premium models', {
        n: plain(words, locale),
      }),
      t('Every channel, including WhatsApp, email and phone'),
      t('Leads and daily summaries'),
    ];
    if (planTier(name) >= PREMIUM_EXTRAS_FROM) {
      list.push(t('Voice conversations in your chat widget'));
      list.push(t('No Easy-Peasy.AI badge'));
    }
    return list;
  };

  const freeFeatures = [
    t('1 agent'),
    t('100 messages a month'),
    t('{{size}} characters of knowledge', {
      size: compact(CHARACTERS_IN_PLAN.Free, locale),
    }),
    t('Website, Telegram, Messenger and Slack'),
    t('Try voice in the Playground'),
  ];

  const scaleProduct = scalePlans[Math.min(scaleIndex, scalePlans.length - 1)];

  return (
    <section id='pricing' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
          <SectionHeading
            title={t('Start free.')}
            tail={t('Upgrade when you need more.')}
          />
          <div
            role='radiogroup'
            aria-label={t('Billing period')}
            className={clsxm(
              'inline-flex flex-none self-start rounded-full p-1 lg:self-auto',
              tone.surface,
              tone.line,
            )}
          >
            {(['month', 'year'] as const).map((option) => (
              <button
                key={option}
                type='button'
                role='radio'
                aria-checked={interval === option}
                onClick={() => setBillingInterval(option)}
                className={clsxm(
                  'flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200',
                  interval === option
                    ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                    : tone.body,
                )}
              >
                {option === 'month' ? t('Monthly') : t('Yearly')}
                {option === 'year' && yearlySaving > 0 && (
                  <span
                    className={clsxm(
                      'rounded-full px-1.5 py-0.5 text-[11px] font-bold',
                      interval === 'year'
                        ? 'bg-emerald-400 text-[#06281b]'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
                    )}
                  >
                    −{yearlySaving}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className='mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4'>
          <PlanCard
            name={t('Free')}
            blurb={t('Try an agent on your website.')}
            price={money(0, 'usd', locale)}
            priceNote={t('Free forever')}
            features={freeFeatures}
            action={
              <Link
                href={user ? '/agents' : '/login?preset=bots'}
                className={clsxm(buttonSecondary, 'w-full')}
              >
                {t('Start free')}
              </Link>
            }
          />
          {CARD_PLANS.map((planName) => {
            const product = byName.get(planName);
            const price = product && priceFor(product, interval);
            const featured = planName === FEATURED_PLAN;
            if (!product || !price) {
              return <PlanSkeleton key={planName} />;
            }
            const current = subscription?.prices?.id === price.id;
            const currency = price.currency ?? 'usd';
            return (
              <PlanCard
                key={planName}
                featured={featured}
                badge={featured ? t('Most popular') : undefined}
                name={planName}
                blurb={
                  {
                    Starter: t('For your first agents on every channel.'),
                    Unlimited: t('For growing teams with more to automate.'),
                    'Unlimited 200': t(
                      'For businesses that run agents everywhere.',
                    ),
                  }[planName]
                }
                price={money(
                  interval === 'year'
                    ? Math.round((price.unit_amount ?? 0) / 12)
                    : (price.unit_amount ?? 0),
                  currency,
                  locale,
                )}
                priceNote={
                  interval === 'year'
                    ? t('per month, billed {{amount}} yearly', {
                        amount: money(price.unit_amount ?? 0, currency, locale),
                      })
                    : t('per month')
                }
                features={featuresFor(planName)}
                action={
                  <button
                    type='button'
                    disabled={current || !!loadingPrice}
                    onClick={() => handleCheckout(price)}
                    className={clsxm(
                      featured ? buttonPrimary : buttonSecondary,
                      'w-full disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                  >
                    {current
                      ? t('Current plan')
                      : loadingPrice === price.id
                        ? t('Loading…')
                        : !user || isFreePlan
                          ? t('Get {{plan}}', { plan: planName })
                          : t('Upgrade')}
                  </button>
                }
              />
            );
          })}
        </div>

        {scaleProduct && (
          <ScalePlans
            plans={scalePlans}
            index={Math.min(scaleIndex, scalePlans.length - 1)}
            onSelect={setScaleIndex}
            interval={interval}
            locale={locale}
            featuresFor={featuresFor}
            priceFor={priceFor}
            money={money}
            loadingPrice={loadingPrice}
            currentPriceId={subscription?.prices?.id}
            onCheckout={handleCheckout}
          />
        )}

        <div
          className={clsxm(
            'mt-10 flex flex-col items-center justify-between gap-4 text-sm sm:flex-row',
            tone.quiet,
          )}
        >
          <p>{t('30-day money-back guarantee. Cancel anytime.')}</p>
          <Link
            href='/pricing'
            className='group inline-flex items-center gap-1.5 font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
          >
            {t('Compare all plans')}
            <ArrowGlyph className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
          </Link>
        </div>
      </div>
    </section>
  );
}

function PlanCard({
  name,
  blurb,
  price,
  priceNote,
  features,
  action,
  featured = false,
  badge,
}: {
  name: string;
  blurb?: string;
  price: string;
  priceNote: string;
  features: string[];
  action: ReactNode;
  featured?: boolean;
  badge?: string;
}) {
  return (
    <div
      className={clsxm(
        'relative flex flex-col rounded-[28px] p-7',
        featured
          ? 'bg-[#10101c] text-white shadow-[0_40px_90px_-40px_rgba(30,20,90,0.8)] ring-1 ring-[#10101c] dark:bg-[#1c1b33] dark:ring-primary-400/30'
          : clsxm(tone.surface, tone.line),
      )}
    >
      <div className='flex items-center justify-between gap-3'>
        <h3
          className={clsxm(
            'text-lg font-semibold',
            featured ? 'text-white' : tone.ink,
          )}
        >
          {name}
        </h3>
        {badge && (
          <span className='rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-white'>
            {badge}
          </span>
        )}
      </div>
      {blurb && (
        <p
          className={clsxm(
            'mt-2 min-h-[2.75rem] text-sm leading-relaxed',
            featured ? 'text-[#b9b9d4]' : tone.body,
          )}
        >
          {blurb}
        </p>
      )}
      <p className='mt-6 flex items-baseline gap-2'>
        <span
          className={clsxm(
            'text-4xl font-semibold tracking-tight tabular-nums',
            featured ? 'text-white' : tone.ink,
          )}
        >
          {price}
        </span>
      </p>
      <p
        className={clsxm(
          'mt-1 min-h-[1.25rem] text-[13px]',
          featured ? 'text-[#9d9dbd]' : tone.quiet,
        )}
      >
        {priceNote}
      </p>
      <div className='mt-6'>{action}</div>
      <ul
        className={clsxm(
          'mt-7 space-y-3 border-t pt-6 text-[14px] leading-6',
          featured
            ? 'border-white/10 text-[#d6d6ea]'
            : 'border-[#0f0f28]/[0.07] dark:border-white/[0.07]',
          !featured && tone.body,
        )}
      >
        {features.map((feature) => (
          <li key={feature} className='flex gap-3'>
            <CheckGlyph
              className={clsxm(
                'mt-1 h-4 w-4',
                featured
                  ? 'text-primary-300'
                  : 'text-primary-600 dark:text-primary-400',
              )}
            />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanSkeleton() {
  return (
    <div
      className={clsxm(
        'h-[34rem] animate-pulse rounded-[28px] motion-reduce:animate-none',
        tone.surface,
        tone.line,
      )}
    />
  );
}

function ScalePlans({
  plans,
  index,
  onSelect,
  interval,
  locale,
  featuresFor,
  priceFor,
  money: formatMoney,
  loadingPrice,
  currentPriceId,
  onCheckout,
}: {
  plans: ProductWithPrice[];
  index: number;
  onSelect: (index: number) => void;
  interval: BillingInterval;
  locale?: string;
  featuresFor: (name: string) => string[];
  priceFor: (
    product: ProductWithPrice,
    wanted: BillingInterval,
  ) => Price | undefined;
  money: (cents: number, currency: string, locale?: string) => string;
  loadingPrice?: string;
  currentPriceId?: string;
  onCheckout: (price: Price) => void;
}) {
  const { t } = useLocale('chatbots');
  const product = plans[index];
  const price = priceFor(product, interval);
  const currency = price?.currency ?? 'usd';
  // The first four lines (agents, actions, knowledge, words) differ by tier.
  const highlights = featuresFor(product.name ?? '').slice(0, 4);

  return (
    <div
      className={clsxm(
        'mt-5 grid gap-8 rounded-[28px] p-7 sm:p-8 lg:grid-cols-[1fr_1.4fr_auto] lg:items-center',
        tone.surface,
        tone.line,
      )}
    >
      <div>
        <h3 className={clsxm('text-lg font-semibold', tone.ink)}>
          {t('Running many agents?')}
        </h3>
        <p className={clsxm('mt-2 text-sm leading-relaxed', tone.body)}>
          {t(
            'Bigger plans with room for up to 160 agents, and weekly automatic retraining on Unlimited 500 and up.',
          )}
        </p>
        <div
          role='radiogroup'
          aria-label={t('Plan size')}
          className='mt-5 flex flex-wrap gap-2'
        >
          {plans.map((plan, planIndex) => (
            <button
              key={plan.id}
              type='button'
              role='radio'
              aria-checked={planIndex === index}
              onClick={() => onSelect(planIndex)}
              className={clsxm(
                'rounded-full px-3.5 py-1.5 text-sm font-semibold tabular-nums transition-colors duration-200',
                planIndex === index
                  ? 'bg-primary-600 text-white'
                  : clsxm(
                      tone.line,
                      tone.body,
                      'hover:text-[#10101c] dark:hover:text-white',
                    ),
              )}
            >
              {(plan.name ?? '').replace('Unlimited ', '')}
            </button>
          ))}
        </div>
      </div>

      <ul className={clsxm('grid gap-3 text-[14px] sm:grid-cols-2', tone.body)}>
        {highlights.map((feature) => (
          <li key={feature} className='flex gap-3'>
            <CheckGlyph className='mt-1 h-4 w-4 text-primary-600 dark:text-primary-400' />
            {feature}
          </li>
        ))}
      </ul>

      <div className='flex flex-col gap-3 lg:w-56'>
        <p>
          <span
            className={clsxm(
              'text-3xl font-semibold tracking-tight tabular-nums',
              tone.ink,
            )}
          >
            {price
              ? formatMoney(
                  interval === 'year'
                    ? Math.round((price.unit_amount ?? 0) / 12)
                    : (price.unit_amount ?? 0),
                  currency,
                  locale,
                )
              : '—'}
          </span>
          <span className={clsxm('ms-1.5 text-sm', tone.quiet)}>
            {t('per month')}
          </span>
        </p>
        {price && (
          <button
            type='button'
            disabled={currentPriceId === price.id || !!loadingPrice}
            onClick={() => onCheckout(price)}
            className={clsxm(
              buttonSecondary,
              'w-full disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {currentPriceId === price.id
              ? t('Current plan')
              : t('Get {{plan}}', { plan: product.name ?? '' })}
          </button>
        )}
      </div>
    </div>
  );
}
